import React, { PureComponent } from 'react';
import { Container, Header, Grid } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import Svg from 'react-inlinesvg';
import {
  SalesToolResults,
  Disclaimer,
  PageHeader,
  SearchResultsPlaceholder,
  StreamModal,
  TotalPill,
  Wrappers
} from '../components';
import { SALES_PAGE } from '../constants';

import { EmailResultsForm, FilterForm } from '../forms';
import { Formatters } from '../utils';
import { compose, graphql } from 'react-apollo';
import { default as getObjectProperty } from 'lodash.get';
import { withPageViewTracking, withAnalytics } from '../analytics';
import { Queries, Mutations, getApiStatus, getApiError } from '../api';
import { TOOLS } from '../constants';
import { screenImageBG, refreshIcon, envelopeIcon } from '../images';

/*
 TODO: This is less than ideal, we're double-querying the endpoint right now
 we should fix that later for performance.
 */
const SalesToolLastUpdatedView = ({ lastUpdated }) => {
  if (lastUpdated) {
    return (
      <time className="text--nowrap">
        <Svg src={refreshIcon} />
        <FormattedMessage
          id="description.date"
          description="Description of date"
          defaultMessage="Last updated on {date}"
          values={{ date: Formatters.date(lastUpdated) }}
        />
      </time>
    );
  }

  return null;
};

SalesToolLastUpdatedView.propTypes = {
  lastUpdated: PropTypes.string
};

const SalesToolLastUpdated = compose(
  graphql(Queries.getLastUpdated, {
    options: {
      fetchPolicy: 'cache-and-network'
    },
    props: props => ({
      ...props,
      lastUpdated: getObjectProperty(props, 'data.lastUpdated', undefined)
    })
  })
)(SalesToolLastUpdatedView);

class GuidedSalesTool extends PureComponent {
  state = {
    queryOptions: null,
    selectedProduct: null,
    formActive: true,
    isEmailFormOpen: false,
    products: null,
    productPrices: [],
    querySelections: [],
    error: null
  };

  static propTypes = {
    intl: intlShape.isRequired,
    analytics: PropTypes.object.isRequired,
    filterGroups: PropTypes.array,
    filterStatus: PropTypes.string,
    filterError: PropTypes.string,
    onSendEmail: PropTypes.func,
    filter: PropTypes.object
  };

  updateResults = (productIDs, productPrices) => {
    this.setState({ products: productIDs, productPrices });
  };

  // for mobile
  toggleSalesToolForm = () => {
    window.scrollTo(0, 0);
    this.setState({ formActive: !this.state.formActive });
  };

  onSubmit = values => {
    const { intl, analytics } = this.props;
    // align the data structure to what's expected in BE
    const queryOptions = Object.keys(values).map(key => ({
      key,
      option: { key: values[key] }
    }));
    // for PDF values, get the localized string from selected query
    let querySelections = {};
    Object.keys(values).forEach(key => {
      querySelections[key] = Formatters.toOptionText(
        intl.formatMessage({
          id: `compareform.${key}.options.${values[key]}`,
          defaultMessage: values[key]
        }),
        values[key]
      );
    });

    analytics.trackFormSubmission({
      Tool: TOOLS.SALES_GUIDANCE
    });

    this.setState({ formActive: false, queryOptions, querySelections });
  };

  // email form for both mobile and desktop
  toggleEmailForm = () => {
    this.setState({ isEmailFormOpen: !this.state.isEmailFormOpen });
  };

  productIDs = products => {
    return products.map(product => product.id).join(', ');
  };

  productDollarAmount = products => {
    // dollar amount of most expensive item in products for analytics
    let value = products
      .map(product => product.price)
      .reduce((max, n) => (n > max ? n : max));
    value = value === null || value === undefined ? '' : value.toString();
    return value;
  };

  onEmailSubmit = async (values, { setSubmitting }) => {
    const { onSendEmail, analytics } = this.props;
    const defaultMessage = (
      <FormattedMessage
        id="error.generic"
        defaultMessage="Something went wrong."
      />
    );
    const analyticsEventName = 'Selling Tool Product Email Sent';
    let analyticsProps = {
      'Email Status': 'failure',
      'Dollar Amount': this.productDollarAmount(this.state.productPrices),
      Tool: 'Selling Tool',
      'Customer email': values.customerEmail,
      'Product SKU': values.products
    };

    // if message is empty string, remove it to trigger default message on backend
    if (!values.message) {
      delete values.message;
    }

    try {
      const res = await onSendEmail(values);
      if (res && res.data.sendPDF && res.data.sendPDF.success) {
        this.toggleEmailForm();
        analyticsProps['Email Status'] = 'Successful';
        analytics.trackEvent(analyticsEventName, analyticsProps);
      } else if (res && res.errors) {
        const error = res.errors[0] ? res.errors[0].message : defaultMessage;
        analytics.trackEvent(analyticsEventName, analyticsProps);
        this.setState({ error });
      }
      setSubmitting(false);
    } catch (error) {
      this.setState({ error: defaultMessage });
      setSubmitting(false);
    }
  };

  render() {
    const { filter } = this.props;
    const {
      queryOptions,
      products,
      error,
      querySelections,
      isEmailFormOpen
    } = this.state;

    const hasResult = products && products.length > 0;

    return (
      <Wrappers.Layout menu>
        <Container as="main">
          <PageHeader overlap>
            <Header as="h1" data-qa="sales-guidance-header">
              <small>Selling</small>
              <FormattedMessage
                id="title.salestool"
                description="Guided Sales Tool page title"
                defaultMessage="Find the right grain handling solution"
              />
            </Header>
            <SalesToolLastUpdated />
          </PageHeader>

          <FilterForm {...filter} onHandleSubmit={this.onSubmit} />

          {products && (
            <TotalPill total={products.length} unit="Result">
              {hasResult && (
                <Grid.Column>
                  <a onClick={this.toggleEmailForm} className="link--icon">
                    <i className="icon" data-qa="email-result-button">
                      <Svg src={envelopeIcon} />
                    </i>
                    <strong>
                      <FormattedMessage
                        id="cta.email_results"
                        description="Email Results button label"
                        defaultMessage="Email Results"
                      />
                    </strong>
                  </a>
                </Grid.Column>
              )}
            </TotalPill>
          )}

          {queryOptions && (
            <SalesToolResults
              queryOptions={queryOptions}
              updateResults={this.updateResults}
            />
          )}

          {!queryOptions && (
            <SearchResultsPlaceholder
              header={
                <FormattedMessage
                  id="subtitle.salestool_intro"
                  description="Selling Tool page intro title"
                  defaultMessage="Let’s find the right one!"
                />
              }
              images={[screenImageBG, screenImageBG, screenImageBG]}
            >
              <p>
                <FormattedMessage
                  id="description.sales_tool"
                  description="Guided Sales Tool page intro description"
                  defaultMessage="Complete the form on the left to find grain handling solutions that best fit your customer's needs."
                />
              </p>
              <Disclaimer page={SALES_PAGE} />
            </SearchResultsPlaceholder>
          )}

          <StreamModal
            isOpen={isEmailFormOpen}
            toggleModal={this.toggleEmailForm}
            size="tiny"
            insetClose={true}
          >
            <EmailResultsForm
              onSubmit={this.onEmailSubmit}
              toggleForm={this.toggleEmailForm}
              products={products}
              querySelections={querySelections}
              error={error}
            />
          </StreamModal>
        </Container>
      </Wrappers.Layout>
    );
  }
}

export default compose(
  graphql(Queries.sellingToolGroups, {
    options: { fetchPolicy: 'cache-and-network' },
    props: props => {
      return {
        filter: {
          filterGroups: props.data && props.data.getSellingToolGroups,
          filterStatus: getApiStatus(props),
          filterError: getApiError(props)
        }
      };
    }
  }),
  graphql(Mutations.sendPDF, {
    props: props => ({
      onSendEmail: email => {
        return props
          .mutate({
            variables: { email }
          })
          .then(res => res);
      },
      ...props
    })
  }),
  withAnalytics,
  withPageViewTracking()
)(injectIntl(GuidedSalesTool));



// WEBPACK FOOTER //
// ./src/pages/GuidedSalesTool.js